'use client';

import { useState, useEffect, useCallback } from 'react';
import type { SpearheadDomain } from '@/lib/types/spearhead';
import type { SpearheadImpact } from '@/lib/types/spearhead';
import { SPEARHEAD_DOMAINS } from '@/lib/types/spearhead';
import { Shield, Crosshair, X } from 'lucide-react';

// ============================================================================
// DOMAIN STYLING
// ============================================================================

const DOMAIN_TOKENS: Record<SpearheadDomain, { hex: string; bar: string; text: string; glow: string }> = {
  alpha:   { hex: '#ef4444', bar: 'bg-red-500',     text: 'text-red-400',     glow: 'rgba(239,68,68,0.55)' },
  bravo:   { hex: '#3b82f6', bar: 'bg-blue-500',    text: 'text-blue-400',    glow: 'rgba(59,130,246,0.55)' },
  charlie: { hex: '#10b981', bar: 'bg-emerald-500', text: 'text-emerald-400', glow: 'rgba(16,185,129,0.55)' },
  delta:   { hex: '#f59e0b', bar: 'bg-amber-500',   text: 'text-amber-400',   glow: 'rgba(245,158,11,0.55)' },
};

const DOMAIN_LABELS: Record<SpearheadDomain, string> = {
  alpha: 'ALPHA',
  bravo: 'BRAVO',
  charlie: 'CHARLIE',
  delta: 'DELTA',
};

const DOMAIN_SHORT: Record<SpearheadDomain, string> = {
  alpha: 'STR',
  bravo: 'END',
  charlie: 'BW',
  delta: 'LOAD',
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function SpearheadImpactToast() {
  const [impact, setImpact] = useState<SpearheadImpact | null>(null);
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  const dismiss = useCallback(() => {
    setAnimateIn(false);
    setTimeout(() => {
      setVisible(false);
      setImpact(null);
    }, 300);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SpearheadImpact>).detail;
      if (!detail) return;

      // Check if any gains actually happened
      const domains: SpearheadDomain[] = ['alpha', 'bravo', 'charlie', 'delta'];
      const hasGains = domains.some(d => detail.gains[d] > 0.01);
      if (!hasGains) return;

      // Persist so the main Operations screen can surface the same
      // before/after AFTER this toast auto-dismisses (the toast is
      // transient; the impact is worth keeping visible).
      try {
        localStorage.setItem('spearhead-last-impact', JSON.stringify({ impact: detail, at: Date.now() }));
      } catch { /* storage full / private mode — non-critical */ }

      setImpact(detail);
      setVisible(true);
      // Trigger animation on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true));
      });

      // Haptic feedback on territory gain
      import('@/lib/haptics').then(({ haptics }) => haptics.territoryGain()).catch(() => {});

      // Auto-dismiss after 8 seconds (longer than normal toast — this is important info)
      setTimeout(dismiss, 8000);
    };

    window.addEventListener('spearheadTerritoryGain', handler);
    return () => window.removeEventListener('spearheadTerritoryGain', handler);
  }, [dismiss]);

  if (!visible || !impact) return null;

  const domains: SpearheadDomain[] = ['alpha', 'bravo', 'charlie', 'delta'];
  const activeDomains = domains.filter(d => impact.gains[d] > 0.01);
  const totalGain = activeDomains.reduce((sum, d) => sum + impact.gains[d], 0);
  const isVictory = !!impact.victoryAchieved;

  // Severity palette
  const accent = isVictory
    ? { hex: '#f59e0b', bar: 'bg-amber-500',   border: 'border-amber-500/60',   brkt: 'border-amber-400/80', text: 'text-amber-400', accentLine: 'via-amber-500/80', glow: 'shadow-[0_0_60px_-12px_rgba(245,158,11,0.55)]' }
    : { hex: '#67e8f9', bar: 'bg-hud-cyan',    border: 'border-hud-cyan/45',    brkt: 'border-hud-cyan/70',  text: 'text-hud-cyan',  accentLine: 'via-hud-cyan/80',  glow: 'shadow-[0_0_50px_-14px_rgba(103,232,249,0.5)]' };

  return (
    <div
      className={`fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-[380px] z-[60] transition-all duration-300 ${
        animateIn
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 -translate-y-4'
      }`}
    >
      <section
        className={`relative overflow-hidden rounded-xl border bg-hud-ink/95 backdrop-blur-md ${accent.border} ${accent.glow}`}
      >
        {/* Severity side bar */}
        <span aria-hidden className={`absolute top-0 bottom-0 left-0 w-1 ${accent.bar}`} />

        {/* Top accent line */}
        <div
          aria-hidden
          className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent ${accent.accentLine} to-transparent`}
        />

        {/* Four corner brackets — ceremonial frame */}
        <span aria-hidden className={`pointer-events-none absolute top-0 left-0 w-3 h-3 border-t border-l ${accent.brkt}`} />
        <span aria-hidden className={`pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r ${accent.brkt}`} />
        <span aria-hidden className={`pointer-events-none absolute bottom-0 left-0 w-3 h-3 border-b border-l ${accent.brkt}`} />
        <span aria-hidden className={`pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r ${accent.brkt}`} />

        {/* Initial confirmation flash — fades out after entry */}
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-0 transition-opacity duration-700`}
          style={{
            background: `radial-gradient(circle at 50% 0%, ${accent.hex}22, transparent 60%)`,
            opacity: animateIn ? 0 : 1,
          }}
        />

        {/* Faint scan line overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 3px)',
          }}
        />

        {/* Classification banner */}
        <header className={`relative flex items-center justify-between px-4 py-1.5 border-b ${accent.border} font-mono text-[9px] uppercase tracking-[0.28em]`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-1 h-1 rounded-full ${accent.bar} animate-hud-blink`} aria-hidden />
            <span className={accent.text}>
              {isVictory ? 'Operation Complete' : 'Territory Update'}
            </span>
          </div>
          <span className="text-slate-500 tabular-nums shrink-0">SYS·SPR·02</span>
        </header>

        {/* Hero readout */}
        <div className="relative px-4 pt-3 pb-2 pl-5">
          <div className="flex items-start gap-3 mb-3">
            <div
              className={`shrink-0 w-10 h-10 rounded-md border ${accent.border} flex items-center justify-center`}
              style={{ background: `radial-gradient(circle at 50% 50%, ${accent.hex}1f, transparent 70%)` }}
            >
              {isVictory ? (
                <Shield className={`w-5 h-5 ${accent.text}`} />
              ) : (
                <Crosshair className={`w-5 h-5 ${accent.text}`} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-heading text-lg font-bold leading-tight ${isVictory ? 'text-amber-300' : 'text-white'}`}>
                {isVictory ? 'Network dismantled' : 'Territory gained'}
              </div>
              <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                <span className="text-slate-300 tabular-nums">+{totalGain.toFixed(1)}%</span>
                <span className="text-slate-700">·</span>
                <span>{activeDomains.length} sector{activeDomains.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              className="shrink-0 text-slate-500 hover:text-slate-200 transition p-1 rounded-sm hover:bg-slate-800/60"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Domain impact bars */}
          <div className="space-y-2">
            {activeDomains.map((d, idx) => {
              const gain = impact.gains[d];
              const prev = impact.prevControl[d];
              const now = impact.newControl[d];
              const t = DOMAIN_TOKENS[d];

              return (
                <div
                  key={d}
                  className="animate-fadeIn"
                  style={{ animationDelay: `${200 + idx * 100}ms` }}
                >
                  <div className="flex items-center justify-between mb-1 font-mono text-[9px] uppercase tracking-[0.18em]">
                    <span className="flex items-center gap-1.5">
                      <span className={`w-0.5 h-2.5 ${t.bar}`} aria-hidden />
                      <span className={`font-bold ${t.text}`}>{DOMAIN_LABELS[d]}</span>
                      <span className="text-slate-600">{DOMAIN_SHORT[d]}</span>
                    </span>
                    <span className="tabular-nums">
                      <span className="text-slate-500">{prev.toFixed(1)}</span>
                      <span className="text-emerald-400 mx-1">+{gain.toFixed(1)}</span>
                      <span className="text-slate-200 font-bold">{now.toFixed(1)}%</span>
                    </span>
                  </div>
                  <div className="relative h-2 bg-slate-900 rounded-sm overflow-hidden border border-slate-800">
                    {/* tick marks at 25/50/75 */}
                    <span aria-hidden className="absolute top-0 bottom-0 left-1/4 w-px bg-slate-700/50" />
                    <span aria-hidden className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-700/50" />
                    <span aria-hidden className="absolute top-0 bottom-0 left-3/4 w-px bg-slate-700/50" />
                    {/* Previous control (dim ghost) */}
                    <div
                      className={`absolute inset-y-0 left-0 ${t.bar}`}
                      style={{ width: `${prev}%`, opacity: 0.25 }}
                    />
                    {/* New control (bright, animated forward from prev) */}
                    <div
                      className="absolute inset-y-0 left-0 transition-all duration-1000 ease-out"
                      style={{
                        width: `${now}%`,
                        background: `linear-gradient(90deg, ${t.hex}cc, ${t.hex})`,
                        boxShadow: `0 0 8px ${t.glow}`,
                        transitionDelay: `${300 + idx * 150}ms`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Victory callout */}
          {isVictory && (
            <div
              className="mt-3 pt-2.5 border-t border-amber-500/30 text-center animate-fadeIn"
              style={{ animationDelay: '600ms' }}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400 font-bold">
                All sectors secured — EREBUS network dismantled
              </div>
            </div>
          )}

          {/* New rewards */}
          {impact.newRewards.length > 0 && (
            <div className="mt-3 pt-2.5 border-t border-slate-800/80">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mb-1.5">
                Unlocked
              </div>
              <div className="space-y-1">
                {impact.newRewards.map(r => (
                  <div
                    key={r}
                    className="flex items-center gap-2 px-2 py-1 rounded-sm border border-amber-500/30 bg-amber-500/5"
                  >
                    <span className="text-amber-400">★</span>
                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">
                      {r.replace(/-/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
